package app.nestbridge.feature.profile.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreBackButton
import com.karawek.core.ui.components.CoreDropdown
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreTextField
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.domain.identity.model.ProfessionalServiceType
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileEvent
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileScreen
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileState
import app.nestbridge.feature.profile.screen.CreateUserProfileEvent
import app.nestbridge.feature.profile.screen.CreateUserProfileState
import app.nestbridge.feature.profile.screen.canContinue
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import nestbridge.composeapp.generated.resources.Res
import nestbridge.composeapp.generated.resources.ic_nestbridge_logo
import org.jetbrains.compose.resources.painterResource
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = CreateProfessionalProfileScreen::class,
    scope = AppScope::class,
)
@Composable
fun CreateProfessionalProfileUi(
    state: CreateProfessionalProfileState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Your professional profile",
        subtitle = "This helps families and care teams know who you are. You’ll need to set this " +
                "up before creating or joining care teams. You only need to do this once.",
        footer = { Footer(state) },
        modifier = modifier,
        onBack = { state.eventSink(CreateProfessionalProfileEvent.Back) },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .windowInsetsPadding(WindowInsets.safeDrawing)
                .padding(vertical = CoreSpacing.lg),
        ) {
            Column(
                verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                    CoreTextField(
                        value = state.displayName,
                        onValueChange = { state.eventSink(CreateProfessionalProfileEvent.UpdateDisplayName(it)) },
                        label = "Display name",
                        placeholder = "How you’d like to appear to families",
                        modifier = Modifier.fillMaxWidth(),
                        error = null,
                    )
                    Text(
                        text = "This is how your name will appear in the app.",
                        style = CoreTypography.caption,
                        color = CoreTheme.colors.textSecondary,
                    )
                }

                CoreTextField(
                    value = state.credentials,
                    onValueChange = { state.eventSink(CreateProfessionalProfileEvent.UpdateCredentials(it)) },
                    label = "Credentials",
                    placeholder = "e.g., OT, PT, SLP, BCBA",
                    modifier = Modifier.fillMaxWidth(),
                    error = null,
                )

                CoreTextField(
                    value = state.practiceName,
                    onValueChange = { state.eventSink(CreateProfessionalProfileEvent.UpdatePracticeName(it)) },
                    label = "Practice or clinic name",
                    placeholder = "e.g., Metro Early Intervention",
                    modifier = Modifier.fillMaxWidth(),
                    error = null,
                )

                Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                    CoreDropdown(
                        value = state.serviceType,
                        onValueChange = { state.eventSink(CreateProfessionalProfileEvent.UpdateServiceType(it)) },
                        options = ProfessionalServiceType.entries,
                        label = "Primary service provided",
                        placeholder = "Select a service type",
                        modifier = Modifier.fillMaxWidth(),
                        error = null,
                        itemLabel = { it.label },
                    )
                    Text(
                        text = "This helps us preselect care team settings. You can change this later.",
                        style = CoreTypography.caption,
                        color = CoreTheme.colors.textSecondary,
                    )
                }
            }

            state.errorMessage?.let {
                Text(
                    text = it,
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.error,
                )
            }
        }
    }
}

@Composable
private fun Footer(state: CreateProfessionalProfileState) =
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        CorePrimaryButton(
            text = if (state.isSaving) "Saving..." else "Continue",
            enabled = state.canContinue(),
            onClick = { state.eventSink(CreateProfessionalProfileEvent.Continue) },
            modifier = Modifier
                .fillMaxWidth(),
        )

        Text(
            text = "You can update this information later in your settings.",
            style = CoreTypography.footnote,
            color = CoreTheme.colors.textSecondary,
        )
    }

@Preview
@Composable
private fun CreateProfessionalProfilePreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        CreateProfessionalProfileUi(
            state = CreateProfessionalProfileState(
                displayName = "Dr. Jessica",
                credentials = "MS, CCC-SLP",
                practiceName = "Jessica Speech Therapy",
                serviceType = ProfessionalServiceType.SpeechLanguageTherapy,
                isSaving = false,
                errorMessage = null,
                eventSink = {},
            ),
        )
    }
}
